/**
 * What the editor accepts when a user adds a picture.
 *
 * The upload panel has always told the user "jpeg or png" while the file picker asked for
 * `image/*` and the drop handler took anything whose type began `image/`, so an AVIF went
 * straight in — and then vanished from the PDF export, because the export engine cannot
 * decode it (SNAP-2282). A third list lived in `FileHandlerPlugin` and allowed WebP on top.
 *
 * One list now backs the rule, the picker's accept string, the panel's label and the warning,
 * so the promise on screen and the behaviour cannot drift apart again.
 *
 * This is a UX guard, not a security control: `POST /image/upload` performs no type check, so
 * a direct caller is unaffected.
 */
export const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif'];

/** For an `<input type="file">`. Only a hint — the dialog can still be switched to any file. */
export const ALLOWED_IMAGE_ACCEPT = ALLOWED_IMAGE_TYPES.join(',');

export const IMAGE_FORMATS_LABEL = 'jpeg, png or gif';

export const IMAGE_TYPE_WARNING = `Only ${IMAGE_FORMATS_LABEL} images are allowed`;

/**
 * A file dragged from some sources arrives with an empty `type`. The old check let that
 * through the net by accident; reject it on purpose, since nothing vouches for the contents.
 */
export const isAllowedImageType = (file: File | null | undefined): boolean =>
  !!file?.type && ALLOWED_IMAGE_TYPES.includes(file.type.toLowerCase());

type ImageResult = {
  complete: boolean;
  width: number;
  height: number;
  src: string;
};

interface ImageCache {
  [key: string]: ImageResult;
}

const IMAGE_CACHE: ImageCache = {};

export function resolveImg(src: string): Promise<ImageResult> {
  return new Promise((resolve, reject) => {
    const result: ImageResult = {
      complete: false,
      width: 0,
      height: 0,
      src,
    };

    if (!src) {
      reject(new Error(`Invalid editor image source: ${src}`));
      return;
    }

    if (IMAGE_CACHE[src]) {
      resolve({ ...IMAGE_CACHE[src] });
      return;
    }

    const img = new Image();

    img.onload = () => {
      result.width = img.width;
      result.height = img.height;
      result.complete = true;

      IMAGE_CACHE[src] = { ...result };
      resolve(result);
    };

    img.onerror = () => {
      reject(new Error(`Failed to load editor image: ${src}`));
    };

    img.src = src;
  });
}

export const enum ImageDisplay {
  INLINE = 'inline',
  BREAK_TEXT = 'block',
  FLOAT_LEFT = 'left',
  FLOAT_RIGHT = 'right',
}
